<template>
    <div class="company-information-address-list">
        <div v-if="loading" class="company-information-address-list__loading-card">
            <div class="company-information-address-list__loading-card-header">
                <CompassSkeleton
                    size="x-large"
                    class="company-information-address-list__loading-card-title"
                />
                <div class="company-information-address-list__loading-card-actions">
                    <CompassSkeleton variant="square" />
                    <CompassSkeleton variant="square" />
                </div>
            </div>
            <div class="company-information-address-list__loading-card-address">
                <CompassSkeleton />
                <CompassSkeleton />
                <CompassSkeleton />
            </div>
        </div>
        <template v-if="!loading">
            <AddressCard
                v-for="address in addresses"
                :key="address.id"
                context=""
                :permissions="permissions"
                :address="address"
                :addressUpdating="addressUpdating"
                :disableSwitches="isPanelOpen"
                @onEdit="openAddress"
                @onDelete="deleteAddress(address)"
                @onDeliveryAddressSwitch="defineDeliveryAddress(address)"
                @onInvoicingAddressSwitch="defineInvoicingAddress(address)"
            />
        </template>
        <CompassDialog
            :visible.sync="showDeleteModal"
            size="small"
            :title="$t.__('common.labels.delete_address_title')"
            :primaryAction="primaryActionDeleteAddress"
            :closeOnClickOverlay="false"
            :secondaryActions="[
                {
                    label: $t.__('common.actions.cancel'),
                    action: () => (showDeleteModal = false),
                },
            ]"
            @closed="showDeleteModal = false"
        >
            <span class="typographies-paragraph-medium-regular">
                {{ $t.__('common.labels.delete_address_description') }}
            </span>
        </CompassDialog>
    </div>
</template>
<script>
import AddressCard from 'vcomponents/common/address/AddressCard.vue';
import { deleteAccountAddress, updateAccountAddress } from 'api/addressesService';
import { createNamespacedHelpers } from 'vuex';
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    components: {
        AddressCard,
    },
    props: {
        addresses: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        isPanelOpen: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isLoading: false,
            addressUpdating: null,
            addressSelected: null,
            permissions: {
                can_edit_addresses: true,
                can_delete_addresses: true,
                can_create_addresses: true,
            },
            showDeleteModal: false,
            primaryActionDeleteAddress: {
                label: this.$t.__('common.actions.delete'),
                type: 'primary-destructive',
                action: () => null,
                loading: false,
            },
        };
    },
    created() {
        this.isLoading = true;
    },
    methods: {
        ...popupPanelMapActions(['open', 'close', 'feedProps']),
        openAddress(address) {
            this.addressSelected = address ?? null;
            this.$emit('panelOpen');
            this.open({
                key: 'addressForm',
                props: {
                    address: address,
                    isAccountAddress: true,
                    callback: () => {
                        this.addressSelected = null;
                        this.$emit('panelClose');
                    },
                },
            });
        },
        deleteAddress(address) {
            this.showDeleteModal = true;
            this.primaryActionDeleteAddress.action = () => {
                this.primaryActionDeleteAddress.loading = true;
                deleteAccountAddress(address.id)
                    .then(() => {
                        const filteredAddresses = this.addresses.filter(
                            (item) => item.id !== address.id,
                        );
                        this.$emit('delete', filteredAddresses);

                        this.showDeleteModal = false;
                        this.$pushToast({
                            type: 'success',
                            text: this.$t.__('common.labels.delete_address_success'),
                            icon: true,
                        });
                    })
                    .catch((e) => {
                        this.$pushToast({
                            type: 'error',
                            text: this.$t.__('common.labels.delete_address_error'),
                            icon: true,
                        });
                        throw e;
                    })
                    .finally(() => (this.primaryActionDeleteAddress.loading = false));
            };
        },
        async defineDeliveryAddress(address) {
            try {
                this.addressUpdating = address.id;
                const currentDeliveryAddress = this.addresses.find(
                    (item) => item.is_delivery_address,
                );

                await updateAccountAddress(address.id, {
                    ...address,
                    is_delivery_address: true,
                });

                // REPLACE WITH EMIT
                const updatedAddresses = this.addresses.map((item) => {
                    if (item.id === currentDeliveryAddress?.id) {
                        return {
                            ...item,
                            is_delivery_address: false,
                        };
                    } else if (item.id === address.id) {
                        return {
                            ...item,
                            is_delivery_address: true,
                        };
                    }
                    return item;
                });
                this.$emit('update', updatedAddresses);

                if (this.addressSelected?.id === currentDeliveryAddress?.id) {
                    this.feedProps({
                        key: 'addressForm',
                        props: {
                            address: {
                                ...this.addressSelected,
                                is_delivery_address: false,
                            },
                        },
                    });
                }
            } finally {
                this.addressUpdating = null;
            }
        },
        async defineInvoicingAddress(address) {
            try {
                this.addressUpdating = address.id;
                const currentInvoicingAddress = this.addresses.find(
                    (item) => item.is_invoicing_address,
                );
                await updateAccountAddress(address.id, {
                    ...address,
                    is_invoicing_address: true,
                });
                const updatedAddresses = this.addresses.map((item) => {
                    if (item.id === currentInvoicingAddress.id) {
                        return {
                            ...item,
                            is_invoicing_address: false,
                        };
                    } else if (item.id === address.id) {
                        return {
                            ...item,
                            is_invoicing_address: true,
                        };
                    }
                    return item;
                });

                this.$emit('update', updatedAddresses);

                if (this.addressSelected?.id === currentInvoicingAddress.id) {
                    this.feedProps({
                        key: 'addressForm',
                        props: {
                            address: {
                                ...this.addressSelected,
                                is_invoicing_address: false,
                            },
                        },
                    });
                }
            } finally {
                this.addressUpdating = null;
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.company-information-address-list {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);

    &__loading-card {
        background-color: var(--colors-common-background-default);
        border-radius: var(--spacing-0);
        box-shadow: var(--elevation-1);
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);
        padding: var(--spacing-3);

        &-header {
            align-items: center;
            display: flex;
            justify-content: space-between;
        }

        &-title {
            height: var(--spacing-4);
            width: 150px;
        }

        &-actions {
            display: flex;
            gap: var(--spacing-1);
        }

        &-address {
            &::v-deep {
                .compass-skeleton {
                    margin: 0 0 var(--spacing-0);
                    width: 130px;

                    &:nth-child(2) {
                        width: 100px;
                    }

                    &:last-child {
                        margin: 0;
                        width: 50px;
                    }
                }
            }
        }
    }
}
</style>
